import Loader from '../loader/loader';
import { Flex } from 'antd';
import styles from './call-recording.module.scss';
import StatusChip from '../status-chip/status-chip';
import Share from '@/assets/icons/share-07.svg?react';
import Recording from '@/assets/icons/recording-02.svg?react';
import FileDownload from '@/assets/icons/file-download-02.svg?react';
import Icon from '@ant-design/icons/lib/components/Icon';
import { StatusChipVariant } from '../status-chip/status-chip.types';
import { Button } from '../button/button';
import AudioPlayer from '../audio-player/audio-player';
import { calculateSecondsToMinutes } from '@/utils/helpers';
import { useEffect, useState } from 'react';
import DateWithDot from '../date-with-dot/date-with-dot';
import { ConnectAgent } from '../connect-agent/connect-agent';
import { Maintenance } from '@/services/maintenances-service/maintenances-service.types';
import { Link } from 'react-router-dom';

interface CallRecordingProps {
  callDuration: number;
  callDate: Date;
  currentTime: string;
  duration: string;
  setCurrentTime: (value: string) => void;
  setDuration: (value: string) => void;
  src: string;
  isLoading?: boolean;
  connectAgent?: boolean;
  status?: StatusChipVariant;
  handleDownloadAsTxt: () => void;
  isDownloadingTxt?: boolean;
  handleShare: () => void;
  handleDownloadAudio: () => void;
  isDownloadingAudio?: boolean;
  smsScore?: number | null;
  smsSummary?: string;
  smsReceived?: boolean;
  maintenances?: Maintenance[];
  relatedCall?: string;
  callType?: string;
}

export default function CallRecording({
  src,
  connectAgent,
  status,
  callDuration,
  callDate,
  isLoading,
  currentTime,
  setCurrentTime,
  duration,
  setDuration,
  handleDownloadAsTxt,
  isDownloadingTxt,
  handleShare,
  handleDownloadAudio,
  isDownloadingAudio,
  smsScore,
  smsSummary,
  smsReceived,
  maintenances,
  relatedCall,
  callType,
}: CallRecordingProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    return () => {
      setIsPlaying(false);
    };
  }, [setIsPlaying]);

  return (
    <Flex vertical align="center">
      {isLoading ? (
        <Flex vertical align="center" className={styles.recordingFallback}>
          <Loader />

          <p>Audio is being processed</p>
        </Flex>
      ) : (
        <AudioPlayer
          duration={duration}
          setDuration={setDuration}
          currentTime={currentTime}
          setCurrentTime={setCurrentTime}
          src={src}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          smsScore={smsScore}
          smsSummary={smsSummary}
          smsReceived={smsReceived}
          maintenances={maintenances}
        />
      )}

      <Flex justify="space-between" gap={20} className={styles.recordingInfo} wrap>
        <Flex align="center" gap={8} wrap>
          <Flex align="center" className={styles.dateContainer}>
            <DateWithDot date={callDate} className={styles.date} />
          </Flex>

          <p className={styles.callDuration}>
            Call duration:{' '}
            <span className={styles.callDurationBold}>
              {calculateSecondsToMinutes(callDuration)}
            </span>
          </p>
          <StatusChip variant={status || 'unresolved'} />

          <Flex className={styles.connectAgent} align="center" gap={8}>
            <p>Connect Agent:</p>
            {relatedCall ? (
              callType === 'AgentCall' ? (
                <Link to={`/conversations/${relatedCall}`}>
                  <ConnectAgent type={connectAgent ? 'outgoing' : 'unanswered'} />
                  <span className={styles.connectAgentLinkText}> AI Call</span>
                </Link>
              ) : (
                <Link to={`/conversations/${relatedCall}`}>
                  <ConnectAgent type={connectAgent ? 'outgoing' : 'unanswered'} />
                </Link>
              )
            ) : (
              <ConnectAgent type={connectAgent ? 'outgoing' : 'unanswered'} />
            )}
          </Flex>
        </Flex>
        <Flex align="center" gap={8} wrap>
          <Button
            loading={isDownloadingTxt}
            buttonVariant="secondary"
            onClick={handleDownloadAsTxt}
            className={styles.button}
          >
            <Icon
              className={styles.buttonIcon}
              component={() => <FileDownload width={16} height={16} />}
            />
            Download as txt
          </Button>
          <Button
            loading={isDownloadingAudio}
            buttonVariant="secondary"
            onClick={handleDownloadAudio}
            className={styles.button}
          >
            <Icon
              className={styles.buttonIcon}
              component={() => <Recording width={16} height={16} />}
            />
            Download audio
          </Button>
          <Button buttonVariant="secondary" onClick={handleShare} className={styles.button}>
            <Icon
              className={styles.buttonIcon}
              component={() => <Share width={16} height={16} />}
            />
            Copy link
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
